import { Badge } from '@components/badge';
import { useDeleteModal } from '@components/generic-modals';
import { IconGear } from '@components/icons/gear';
import { IconRemove } from '@components/icons/remove';
import { IconReplace } from '@components/icons/replace';
import { Menu } from '@components/menu';
import { useScopedTranslate } from '@locale/translate';

import { JobTrigger } from './api';

/**
 * Width for every Menu rendering TriggerActionMenuItems. The longest label,
 * "Can't edit — journey unavailable", needs this much to render in full beside
 * its gear icon — at the Menu's default width it clipped to "Can't edit —
 * journey un…", hiding the very text that explains why the item is disabled.
 * Shared so the sidebar row menu and the stage-header chip menu cannot drift.
 */
export const TRIGGER_MENU_WIDTH = 320;

export type TriggerActionMenuItemsProps = {
  trigger: JobTrigger;
  onEditConfig: () => void;
  onReplace: () => void;
  onDelete: () => void;
  isMutating: boolean;
  /** Prefixes each item's data-cy: `${dataCyPrefix}-edit-config` / `-replace` / `-remove`. */
  dataCyPrefix: string;
};

/**
 * The Edit configuration / Replace / Remove actions shared by every surface
 * with a trigger menu (the triggers-sidebar rows and the stage-header chip).
 * Render inside a <Menu>.
 */
export const TriggerActionMenuItems = ({
  trigger,
  onEditConfig,
  onReplace,
  onDelete,
  isMutating,
  dataCyPrefix,
}: TriggerActionMenuItemsProps) => {
  const { t } = useScopedTranslate(locale);

  return (
    <>
      {trigger.isAvailable ? (
        <Menu.Item
          icon={<IconGear />}
          label={t('editConfiguration')}
          dataCy={`${dataCyPrefix}-edit-config`}
          onSelect={onEditConfig}
        />
      ) : (
        <Menu.Item
          icon={<IconGear />}
          label={t('editConfigUnavailable')}
          dataCy={`${dataCyPrefix}-edit-config`}
          isDisabled
          tooltip={t('unavailableTooltip')}
        />
      )}
      <Menu.Item
        icon={<IconReplace />}
        label={t('replace')}
        dataCy={`${dataCyPrefix}-replace`}
        onSelect={onReplace}
        isDisabled={isMutating}
      />
      <Menu.Item
        icon={<IconRemove />}
        label={t('remove')}
        variant="danger"
        dataCy={`${dataCyPrefix}-remove`}
        onSelect={onDelete}
        isDisabled={isMutating}
      />
    </>
  );
};

type TriggerUnavailableBadgeProps = {
  dataCy: string;
};

export const TriggerUnavailableBadge = ({ dataCy }: TriggerUnavailableBadgeProps) => {
  const { t } = useScopedTranslate(locale);
  return (
    <Badge
      data-cy={dataCy}
      variant="orange"
      text={t('unavailable')}
      tooltip={t('unavailableTooltip')}
      // Badge renders a styled div and forwards unknown DOM attributes, so an
      // aria-label alone is not exposed as a labelled element to assistive
      // tech in most cases — role="img" is what makes it honoured.
      role="img"
      aria-label={t('unavailableTooltip')}
    />
  );
};

/**
 * Delete confirmation shared by every remove-trigger surface. The same journey
 * can be attached to several stages and outcomes, so the confirmation names
 * the slot (rowLabel) as well as the journey.
 *
 * The generic modal's "this can not be undone" line is suppressed: removing a
 * trigger destroys no applicant data and is reversed by adding it back.
 */
export const useRemoveTriggerConfirmation = () => {
  const { t } = useScopedTranslate(locale);
  const showDeleteModal = useDeleteModal();

  return (trigger: JobTrigger, rowLabel: string, onConfirm: () => void | Promise<unknown>) =>
    showDeleteModal({
      title: t('removeTitle'),
      // An unavailable journey no longer exists upstream, so its title arrives
      // as "" — interpolating that reads "…remove  from Stage 3?".
      description: trigger.journeyTitle
        ? t('removeConfirmation', { journeyTitle: trigger.journeyTitle, rowLabel })
        : t('removeConfirmationUntitled', { rowLabel }),
      buttonText: t('remove'),
      buttonIcon: <IconRemove />,
      hideCannotBeUndone: true,
      onConfirm,
    });
};

const locale = {
  en: {
    editConfiguration: 'Edit configuration',
    editConfigUnavailable: "Can't edit — journey unavailable",
    replace: 'Replace',
    remove: 'Remove',
    unavailable: 'Unavailable',
    unavailableTooltip:
      'This journey is no longer available for this job. Use Replace to pick another one.',
    removeTitle: 'Remove journey trigger?',
    removeConfirmation: 'Are you sure you want to remove {journeyTitle} from {rowLabel}?',
    removeConfirmationUntitled: 'Are you sure you want to remove this journey from {rowLabel}?',
  },
  is: {
    editConfiguration: 'Breyta stillingum',
    editConfigUnavailable: 'Ekki hægt að breyta. Journey-ferlið er ekki í boði.',
    replace: 'Skipta út',
    remove: 'Fjarlægja',
    unavailable: 'Ekki í boði',
    unavailableTooltip:
      'Þetta Journey-ferli er ekki lengur í boði fyrir þetta starf. Veldu „Skipta út“ til að velja annað.',
    removeTitle: 'Fjarlægja ræsingarreglu fyrir Journey-ferli?',
    removeConfirmation: 'Viltu örugglega fjarlægja „{journeyTitle}“ úr „{rowLabel}“?',
    removeConfirmationUntitled: 'Viltu örugglega fjarlægja þetta Journey-ferli úr „{rowLabel}“?',
  },
};
